import { Box, ToggleButtonGroup } from "@mui/joy";
import { ClimbType } from "griptonite-api-client";

import { BoxLink, ButtonLink } from "@/components/Links";
import { useCurrentRouteMatch } from "@/hooks/useCurrentRouteMatch";
import { useCurrentClimbType, useCurrentVenueQuery } from "@/hooks/venues";

import { useSidebarExpanded } from "./SidebarExpanded";

/**
 * Link options for the current page with a different climb type.
 *
 * The selector shows on every route with a `$climbType` param, so the target
 * route isn't known until runtime. The cast types it as the routes page,
 * which takes the same params.
 */
const useSwitchClimbTypeLink = () => {
  const currentRoute = useCurrentRouteMatch();
  return (climbType: ClimbType) => ({
    to: currentRoute.fullPath as "/gym/$venueUuid/routes/$climbType",
    params: { ...currentRoute.params, climbType } as {
      venueUuid: string;
      climbType: string;
    },
    search: true as const,
  });
};

const ClimbTypeSelector = () => {
  const { data: currentVenue } = useCurrentVenueQuery();

  const climbType = useCurrentClimbType();

  const isExpanded = useSidebarExpanded();

  const currentRoute = useCurrentRouteMatch();
  const switchClimbTypeLink = useSwitchClimbTypeLink();

  if (!("venueUuid" in currentRoute.params) || !("climbType" in currentRoute.params)) {
    return null;
  }
  if (currentVenue && !currentVenue.climbTypes) {
    return null;
  }
  if (!currentVenue || currentVenue.climbTypes.length < 2) {
    return null;
  }

  if (!isExpanded) {
    return (
      <BoxLink
        {...switchClimbTypeLink(climbType === "BOULDER" ? "SPORT" : "BOULDER")}
        sx={{
          paddingX: 2,
          paddingY: 1,
          textDecoration: "none",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            background: "white",
            color: climbType === "BOULDER" ? "#2277C2" : "#BB6BD9",
            border: "1px solid",
            borderColor: "neutral.200",
            borderRadius: "100%",
            width: 22,
            height: 22,
            fontWeight: "bold",
          }}
        >
          {climbType?.slice(0, 1)}
        </Box>
      </BoxLink>
    );
  }

  return (
    <ToggleButtonGroup
      sx={[
        {
          marginX: 2,
          marginLeft: 6,
          padding: 0.5,
          borderRadius: "8px",
          backgroundColor: "white",
          "--ButtonGroup-connected": 0,
          ".MuiButton-root": {
            flex: 1,
            paddingY: 0.5,
            color: "neutral.900",
            backgroundColor: "transparent",
            fontWeight: "normal",
            minHeight: 0,
            "&.active, &[aria-pressed=true]": {
              backgroundColor: "neutral.200",
              borderRadius: "6px",
              fontWeight: "bold",
            },
          },
        },
      ]}
      variant="soft"
      color="neutral"
      value={climbType}
    >
      <ButtonLink {...switchClimbTypeLink("BOULDER")} value="BOULDER">
        Boulder
      </ButtonLink>
      <ButtonLink {...switchClimbTypeLink("SPORT")} value="SPORT">
        Sport
      </ButtonLink>
    </ToggleButtonGroup>
  );
};

export default ClimbTypeSelector;
